import {
  BusinessGoal,
  BusinessGoalCategory,
  BusinessGoalMilestone,
  BusinessGoalPriority,
  BusinessProfile,
  UserTask,
  UserTaskItemStatus,
} from '__generated__/api-types-and-hooks'
import { COACH_LOGIC_IDS } from 'constants/coachLogic'
import { AppPillColorTheme } from 'components/Common/AppPillTextBox/AppPillTextBox.component'
import parsePhoneNumberFromString from 'libphonenumber-js'
import moment, { Moment } from 'moment'
import { BadgeColor } from 'components/Common/AppBadge/AppBadge.component'
import { getHoursCeiling } from 'components/AppKanbanBoard/AppTask/AppTaskHook/AppTaskHook.hook'
import { ColorType } from './colorUtils'

export const getColorThemeForGoalPriority = (goalPriority: string): AppPillColorTheme => {
  switch (goalPriority) {
    case BusinessGoalPriority.LowPriority:
      return AppPillColorTheme.GREEN
    case BusinessGoalPriority.MediumPriority:
      return AppPillColorTheme.ORANGE
    case BusinessGoalPriority.HighPriority:
      return AppPillColorTheme.RED
    default:
      return AppPillColorTheme.BLUE
  }
}

export const getColorThemeForTaskStatus = (taskStatus: UserTaskItemStatus): AppPillColorTheme => {
  switch (taskStatus) {
    case UserTaskItemStatus.Todo || UserTaskItemStatus.Creating:
      return AppPillColorTheme.GREY
    case UserTaskItemStatus.InProgress:
      return AppPillColorTheme.BLUE
    case UserTaskItemStatus.Done:
      return AppPillColorTheme.GREEN
    default:
      return AppPillColorTheme.RED
  }
}

export const getColorThemeForGoalCategory = (goalCategory: string): AppPillColorTheme => {
  switch (goalCategory) {
    case BusinessGoalCategory.Marketing:
      return AppPillColorTheme.GREEN
    case BusinessGoalCategory.Hr:
      return AppPillColorTheme.ORANGE
    case BusinessGoalCategory.Sales:
      return AppPillColorTheme.RED
    case BusinessGoalCategory.Legal:
      return AppPillColorTheme.BROWN
    case BusinessGoalCategory.Communications:
      return AppPillColorTheme.BLUE
    case BusinessGoalCategory.CustomerService:
      return AppPillColorTheme.LIME
    case BusinessGoalCategory.Finance:
      return AppPillColorTheme.SKY
    case BusinessGoalCategory.InformationTechnology:
      return AppPillColorTheme.YELLOW
    case BusinessGoalCategory.Operations:
      return AppPillColorTheme.PURPLE
    case BusinessGoalCategory.Production:
      return AppPillColorTheme.PURPLE
    case BusinessGoalCategory.ResearchAndDevelopment:
      return AppPillColorTheme.ORANGE
    case BusinessGoalCategory.Strategy:
      return AppPillColorTheme.BROWN
    default:
      return AppPillColorTheme.GREEN
  }
}

export const fieldBooleanOptions = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
]

export const tasksCompletedCount = (goals) => {
  return goals?.reduce((totalCount, goal) => {
    if (goal.plan && goal.plan.milestones) {
      return (
        totalCount +
        goal.plan.milestones.reduce((milestoneCount, milestone) => {
          if (milestone.tasks) {
            const doneCount = milestone.tasks.filter((task) => task.status === 'DONE').length
            return milestoneCount + doneCount
          }
          return milestoneCount
        }, 0)
      )
    }
    return totalCount
  }, 0)
}

export const allTasksTodo = (plan) => {
  if (plan) {
    for (const milestone of plan.milestones) {
      for (const task of milestone.tasks) {
        if (task.status !== 'TODO') {
          return false
        }
      }
    }
    return true
  }
  return true
}

export const calculateTasksProgress = (tasks) => {
  if (tasks?.length > 0) {
    const totalTasks = tasks?.length
    let statusCounts = {
      DONE: 0,
      TODO: 0,
      IN_PROGRESS: 0,
    }

    tasks.forEach((task) => {
      if (task.status in statusCounts) {
        statusCounts[task.status]++
      }
    })
    if (statusCounts.DONE === totalTasks) return 100
    if (statusCounts.TODO === totalTasks) return 0

    const progress = (statusCounts.IN_PROGRESS / totalTasks) * 100
    return Math.round(progress)
  } else return 0
}

export const getLatestEndDate = (tasks) => {
  if (tasks?.length === 0) return ''

  const latestDate = tasks?.reduce((latest, task) => {
    const endDate = moment(task?.expectedEndDate)
    return endDate.isAfter(latest) ? endDate : latest
  }, moment(0))

  return latestDate?.format('MMMM Do, YYYY')
}

const goalPriorityBadgeColors: Record<BusinessGoalPriority, BadgeColor> = {
  LOW_PRIORITY: 'Green',
  MEDIUM_PRIORITY: 'Orange',
  HIGH_PRIORITY: 'Red',
}

const statusBadgeColors: Record<UserTaskItemStatus, BadgeColor> = {
  TODO: 'Grey',
  IN_PROGRESS: 'Primary',
  DONE: 'Green',
  PAUSED: 'Grey',
  ARCHIVED: 'Grey',
  BLOCKED: 'Red',
  CREATING: 'Grey',
  DELETED: 'Grey',
  DRAFT: 'Grey',
  PENDING: 'Grey',
}

// comment for now, just in case we need it
// const goalCategoryBadgeColors: Record<BusinessGoalCategory, BadgeColor> = {
//   MARKETING: 'Green',
//   HR: 'Orange',
//   SALES: 'Red',
//   LEGAL: 'Grey',
//   COMMUNICATIONS: 'Primary',
//   CUSTOMER_SERVICE: 'Primary',
//   FINANCE: 'Red',
//   INFORMATION_TECHNOLOGY: 'Red',
//   OPERATIONS: 'Primary',
//   PRODUCTION: 'Grey',
//   RESEARCH_AND_DEVELOPMENT: 'Orange',
//   STRATEGY: 'Orange',
// }

// Function to get colors for goal category
export const getColorForGoalCategory = (goalCategory: BusinessGoalCategory): BadgeColor => {
  return 'Grey'
}

// Function to get colors for goal priority
export const getColorForGoalPriority = (goalCategory: BusinessGoalPriority): BadgeColor => {
  return goalPriorityBadgeColors[goalCategory] || 'Grey'
}

// Function to get colors for status
export const getColorForStatus = (status: UserTaskItemStatus): BadgeColor => {
  return statusBadgeColors[status] || 'Grey'
}

export const formatMobileNumber = (mobileNumber: string) => {
  if (!mobileNumber) return ''

  // Try parsing with default US country code first
  let phoneNumber = parsePhoneNumberFromString(mobileNumber, 'US')

  // If that fails, try parsing as-is (in case it already has country code)
  if (!phoneNumber && !mobileNumber.startsWith('+')) {
    phoneNumber = parsePhoneNumberFromString(`+${mobileNumber}`)
  }

  if (!phoneNumber) {
    return ''
  }

  return phoneNumber.formatInternational()
}

// Calculate the number of working days (Monday to Friday) between two dates, inclusive of startDate and endDate
export const calculateWorkingDaysBetweenTwoDates = (startDate: Moment, endDate: Moment): number => {
  let count = 0
  let current = startDate.clone()

  while (!current.isAfter(endDate, 'day')) {
    const dayOfWeek = current.day()
    if (dayOfWeek !== 0 && dayOfWeek !== 6) count += 1 // Exclude weekends
    current.add(1, 'day')
  }
  return count
}

// Calculate total estimated hours for allTasks with start dates after the expected end date
export const calculateTotalEstimationHoursOfUserTasks = (
  allTasks: UserTask[],
  expectedEndDateofCurrentTask: Moment,
  taskId: string
): number => {
  return allTasks.reduce((sum, task) => {
    const estimatedHours = getHoursCeiling(task?.estimatedEffortInMinutes)
    // Ignore tasks with the specified taskId
    if (task.taskId === taskId || task.status === UserTaskItemStatus.Done) {
      return sum
    }
    const taskStartDate = moment(task.startDate)
    if (taskStartDate.isAfter(expectedEndDateofCurrentTask, 'day')) {
      return sum + (estimatedHours || 0)
    }
    return sum
  }, 0)
}

// Checks if a given date lies within the current week and if it is in the past
export const isDateInCurrentWeekAndPast = (date: string): boolean => {
  const targetDate = moment(date)
  const startOfWeek = moment().startOf('week')
  const endOfWeek = moment().endOf('week')
  const isInCurrentWeek = targetDate.isBetween(startOfWeek, endOfWeek, 'day', '[]')
  const isInPast = targetDate.isBefore(moment(), 'day')
  return isInCurrentWeek || isInPast
}

export const calculateNewGoalEndDate = (goalExpectedEndDate, hoursToBeAddedd, hoursSpentPerDay) => {
  let extraDaysRequired = Math.ceil(Math.abs(hoursToBeAddedd) / hoursSpentPerDay)
  // Start with the preliminary new end date
  let newGoalEndDate = moment(goalExpectedEndDate)
  // Loop through each additional day, checking for weekends
  while (extraDaysRequired > 0) {
    newGoalEndDate = newGoalEndDate.add(1, 'days')
    // If the day is a weekday, reduce the extra days counter
    if (newGoalEndDate.day() !== 6 && newGoalEndDate.day() !== 0) {
      extraDaysRequired -= 1
    }
  }
  return newGoalEndDate.format('YYYY-MM-DD')
}

export const createWeeklyMilestones = (tasks: UserTask[]) => {
  // Filter out tasks with invalid or missing start dates
  const validTasks = tasks.filter((task) => {
    if (!task.startDate) return false
    const momentDate = moment(task.startDate)
    return momentDate.isValid()
  })

  if (validTasks.length === 0) {
    return []
  }

  // Sort tasks by start date using moment
  validTasks.sort((a, b) => moment(a.startDate).diff(moment(b.startDate)))

  const milestones: BusinessGoalMilestone[] = []
  let currentWeekStart: Moment | null = null
  let weekCount = 1

  if (validTasks.length > 0) {
    const earliestTaskDate = moment(validTasks[0].startDate)
    const latestTaskDate = moment(getLatestEndDate(validTasks), 'MMMM Do, YYYY')

    const currentTaskDate = earliestTaskDate.clone().startOf('isoWeek') // Start date is Monday of week of task

    while (currentTaskDate.isSameOrBefore(latestTaskDate)) {
      currentWeekStart = currentTaskDate.clone().startOf('isoWeek') // Start date is Monday of week of task

      milestones.push({
        name: `Week ${weekCount++}`,
        startDate: currentWeekStart.format('YYYY-MM-DD'),
        expectedEndDate: currentWeekStart.clone().endOf('isoWeek').format('YYYY-MM-DD'),
        tasks: [],
      })

      currentTaskDate.add(7, 'days')
    }

    for (let task of validTasks) {
      const taskStartDate = moment(task.startDate).format('YYYY-MM-DD')

      const currentMilestone = milestones.find(
        (milestone) =>
          milestone.startDate! <= taskStartDate && milestone.expectedEndDate! >= taskStartDate
      )
      if (!currentMilestone)
        throw new Error(
          `Unexpected error - milestone for task starting ${taskStartDate} not found.`
        )
      if (currentMilestone) {
        currentMilestone?.tasks?.push(task)
      }
    }
  }
  return milestones
}
export const removeBusinessSocialMediaFieldsLinks = (businessProfile: BusinessProfile) => {
  businessProfile.facebook = businessProfile?.facebook?.replace('www.facebook.com/', '')
  businessProfile.instagram = businessProfile?.instagram?.replace('www.instagram.com/', '')
  businessProfile.linkedin = businessProfile?.linkedin?.replace('www.linkedin.com/in/', '')
  businessProfile.tiktok = businessProfile?.tiktok?.replace('www.tiktok.com/', '')
  businessProfile.twitter = businessProfile?.twitter?.replace('www.x.com/', '')
  businessProfile.youtube = businessProfile?.youtube?.replace('www.youtube.com/', '')

  return businessProfile
}
export const addBusinessSocialMediaFieldsLinks = (businessProfile: BusinessProfile) => {
  businessProfile.facebook = businessProfile?.facebook
    ? `www.facebook.com/${businessProfile?.facebook}`
    : ''
  businessProfile.instagram = businessProfile?.instagram
    ? `www.instagram.com/${businessProfile?.instagram}`
    : ''
  businessProfile.linkedin = businessProfile?.linkedin
    ? `www.linkedin.com/in/${businessProfile?.linkedin}`
    : ''
  businessProfile.tiktok = businessProfile?.tiktok
    ? `www.tiktok.com/${businessProfile?.tiktok}`
    : ''
  businessProfile.twitter = businessProfile?.twitter ? `www.x.com/${businessProfile?.twitter}` : ''
  businessProfile.youtube = businessProfile?.youtube
    ? `www.youtube.com/${businessProfile?.youtube}`
    : ''

  return businessProfile
}
export const formatTime = (timeInMinutes) => {
  const hours = Math.floor(timeInMinutes / 60)
  const minutes = timeInMinutes % 60

  const updateHour = `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  const updateMin = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`

  return `${updateHour} ${updateMin}`
}

export const formatTimeFieldValue = (timeInMinutes) => {
  const hours = Math.floor(timeInMinutes / 60)
  const minutes = timeInMinutes % 60

  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')

  return `${formattedHours}:${formattedMinutes}`
}

export const getUpdatedMessageState = (
  labelName: string,
  value: string[],
  currentState: { label: string; value: string[] }[]
): { label: string; value: string[] }[] => {
  const existingIndex = currentState.findIndex((item) => item.label === labelName)

  if (existingIndex > -1) {
    // Update the existing entry
    const updated = [...currentState]
    updated[existingIndex] = { label: labelName, value }
    return updated
  }

  // Add a new entry
  return [...currentState, { label: labelName, value }]
}

/**
 * Check if a goal is suggested by Coach Logic
 * @param goal - The BusinessGoal to check
 * @returns true if the goal is suggested by Coach Logic, false otherwise
 */
export const isGoalSuggestedByCoachLogic = (goal: BusinessGoal): boolean => {
  if (!goal.suggestedById) {
    return false
  }
  return COACH_LOGIC_IDS.includes(goal.suggestedById as typeof COACH_LOGIC_IDS[number])
}

// Generic function to sort BusinessGoal array based on 'createdAt' field
export const sortBusinessGoals = (
  goals: BusinessGoal[],
  sortOrder: 'ascending' | 'descending' = 'ascending'
): BusinessGoal[] => {
  return goals.sort((a, b) => {
    const comparison = moment(a.createdAt).isBefore(moment(b.createdAt)) ? -1 : 1
    return sortOrder === 'ascending' ? comparison : -comparison
  })
}

export const getRandomColor = () => {
  const colors = ['Grey', 'Primary', 'Red', 'Green', 'Orange']
  const randomIndex = Math.floor(Math.random() * colors.length)
  return colors[randomIndex] as ColorType
}
